/*
 * Adventure Bound landing pages: scratch-off front card (sections/ab-sides.liquid).
 * A silver coating canvas sits over the card's window and the shopper erases it with the pointer
 * (mouse, touch, pen). Rules (Austin comment 7):
 *  - brush radius is 7% of the card width (BRUSH), strokes use destination-out
 *  - progress is a coarse grid of touched cells (no getImageData, so a cross-origin CDN texture cannot break it)
 *  - at 55% cleared (REVEAL_AT) the rest fades away; prefers-reduced-motion skips the fade
 *  - touch-action: none is set on the canvas only (CSS), so the rest of the card still scrolls the page
 *  - the window is role="button", tabindex 0, label "Scratch to reveal the card"; Enter or Space reveals at once
 *    (the card text is already in the image alt, so screen readers lose nothing)
 *  - lazy: the canvas is only built when the card is near the viewport; resize and devicePixelRatio are handled
 *    (the bitmap is repainted only before the first stroke, so a mobile URL bar resize never wipes progress)
 * Idempotent; re-runs on theme editor section load.
 */
(function () {
  'use strict';

  var BRUSH = 0.07; // brush radius as a share of the card width
  var REVEAL_AT = 0.55; // share of the window cleared before the rest fades away
  var GRID_X = 36; // coarse progress grid (rows follow the window aspect)
  var LABEL = 'Scratch to reveal the card';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var silverImg = null;
  var silverSrc = '';

  function qsa(root, sel) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function loadSilver(src, cb) {
    if (!src) return cb(null);
    if (silverImg && silverSrc === src) {
      if (silverImg.complete && silverImg.naturalWidth) return cb(silverImg);
      silverImg.addEventListener('load', function () { cb(silverImg); });
      silverImg.addEventListener('error', function () { cb(null); });
      return;
    }
    silverSrc = src;
    silverImg = new Image();
    silverImg.addEventListener('load', function () { cb(silverImg); });
    silverImg.addEventListener('error', function () { cb(null); });
    silverImg.src = src;
  }

  function setup(wrap) {
    if (wrap.getAttribute('data-ab-scratch-init')) return;
    wrap.setAttribute('data-ab-scratch-init', '1');
    var win = wrap.querySelector('.ab-scratch__win');
    if (!win) return;
    var state = { done: false, activated: false, canvas: null };

    // Keyboard and assistive tech work from the first moment (not only once the canvas is built):
    // Enter, Space or a click without a pointer (detail 0) reveals the card at once.
    win.setAttribute('role', 'button');
    win.setAttribute('tabindex', '0');
    win.setAttribute('aria-label', LABEL);

    state.reveal = function (how) {
      if (state.done) return;
      state.done = true;
      wrap.classList.add('is-revealed');
      win.removeAttribute('role');
      win.setAttribute('tabindex', '-1');
      if (state.cleanup) state.cleanup();
      win.removeAttribute('aria-label');
      win.style.pointerEvents = 'none';
      var canvas = state.canvas;
      if (canvas) {
        var remove = function () { if (canvas.parentNode) canvas.parentNode.removeChild(canvas); };
        if (reduceMotion) remove();
        else setTimeout(remove, 600);
      }
      try { wrap.dispatchEvent(new CustomEvent('ab:scratch-reveal', { bubbles: true, detail: { how: how } })); } catch (e) {}
    };

    win.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        state.reveal('keyboard');
      }
    });
    win.addEventListener('click', function (e) {
      if (e.detail === 0) state.reveal('keyboard');
    });
    win.addEventListener('focus', function () { activate(wrap, win, state); });

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) {
            io.disconnect();
            activate(wrap, win, state);
          }
        });
      }, { rootMargin: '300px 0px' });
      io.observe(wrap);
    } else {
      activate(wrap, win, state);
    }
  }

  function activate(wrap, win, state) {
    if (state.activated || state.done) return;
    state.activated = true;
    var canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    var ctx = canvas.getContext('2d');
    if (!ctx) { // no canvas support: leave the card as it is, with no dead button
      win.removeAttribute('role');
      win.removeAttribute('tabindex');
      win.removeAttribute('aria-label');
      return;
    }
    win.appendChild(canvas);
    state.canvas = canvas;

    var w = 0, h = 0; // window size in CSS px (layout size, not affected by the card rotation)
    var gx = GRID_X, gy = 1, cells = null, cleared = 0, total = 1;
    var down = false, last = null, moved = false;
    var silver = null;

    function paint() {
      if (moved || state.done) return;
      w = win.clientWidth; h = win.clientHeight;
      if (!w || !h) return;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = '#bfc3c9';
      ctx.fillRect(0, 0, w, h);
      if (silver) {
        // cover-fit the silver texture
        var iw = silver.naturalWidth, ih = silver.naturalHeight;
        var s = Math.max(w / iw, h / ih);
        var sw = w / s, sh = h / s;
        ctx.drawImage(silver, (iw - sw) / 2, (ih - sh) / 2, sw, sh, 0, 0, w, h);
      }
      gy = Math.max(1, Math.round(gx * h / w));
      total = gx * gy;
      cells = new Uint8Array(total);
      cleared = 0;
    }

    paint();
    loadSilver(wrap.getAttribute('data-silver'), function (img) {
      silver = img;
      if (img) paint();
    });

    function radius() {
      return Math.max(12, wrap.clientWidth * BRUSH);
    }

    function pos(e) {
      // offsetX/Y are in the canvas's own (unrotated) CSS pixels even when the card is rotated
      var x = e.offsetX, y = e.offsetY;
      if (typeof x !== 'number' || e.target !== canvas) {
        var r = canvas.getBoundingClientRect();
        x = e.clientX - r.left; y = e.clientY - r.top;
      }
      return { x: x, y: y };
    }

    function mark(p, rad) {
      var x0 = Math.max(0, Math.floor((p.x - rad) / w * gx)), x1 = Math.min(gx - 1, Math.floor((p.x + rad) / w * gx));
      var y0 = Math.max(0, Math.floor((p.y - rad) / h * gy)), y1 = Math.min(gy - 1, Math.floor((p.y + rad) / h * gy));
      for (var y = y0; y <= y1; y++) {
        for (var x = x0; x <= x1; x++) {
          var cx = (x + 0.5) / gx * w, cy = (y + 0.5) / gy * h;
          if (Math.pow(cx - p.x, 2) + Math.pow(cy - p.y, 2) <= rad * rad && !cells[y * gx + x]) {
            cells[y * gx + x] = 1;
            cleared++;
          }
        }
      }
    }

    function line(a, b) {
      var rad = radius();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = rad * 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
      var steps = Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / (rad * 0.5)));
      for (var i = 0; i <= steps; i++) {
        mark({ x: a.x + (b.x - a.x) * i / steps, y: a.y + (b.y - a.y) * i / steps }, rad * 0.95);
      }
    }

    function check() {
      if (!state.done && total > 1 && cleared / total >= REVEAL_AT) state.reveal('scratch');
    }

    canvas.addEventListener('pointerdown', function (e) {
      if (state.done || (e.pointerType === 'mouse' && e.button !== 0)) return;
      down = true;
      moved = true;
      last = pos(e);
      try { canvas.setPointerCapture(e.pointerId); } catch (x) {}
      line(last, { x: last.x + 0.1, y: last.y });
      e.preventDefault();
    });
    canvas.addEventListener('pointermove', function (e) {
      if (!down || state.done) return;
      var p = pos(e);
      line(last, p);
      last = p;
      check();
    });
    function up() {
      down = false;
      check();
    }
    canvas.addEventListener('pointerup', up);
    canvas.addEventListener('pointercancel', up);
    canvas.addEventListener('contextmenu', function (e) { e.preventDefault(); });

    // resize and devicePixelRatio: repaint only before the first stroke
    var ro = null;
    if (window.ResizeObserver) {
      ro = new ResizeObserver(function () {
        if (!state.done && !moved && Math.abs(win.clientWidth - w) + Math.abs(win.clientHeight - h) > 1) paint();
      });
      ro.observe(win);
    }
    function onResize() { paint(); }
    window.addEventListener('resize', onResize);
    state.cleanup = function () {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }

  function init(root) {
    qsa(root, '[data-ab-scratch]').forEach(setup);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target || document);
  });
})();
